import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import {
  GetBuyerResponse,
  AddNCConfigRequest,
  AddNCConfigResponse,
  GetNCConfigResponse,
  SendRNDataRequest,
  SendRNDataResponse,
  GendRNDataResponse,
  ReportPreferences,
  ReportPreferencesPayload,
} from '../../interfaces/buyer.interface';
import {
  BuyerServiceAccountTokenRes,
  GetPDSettingsResponse,
  NcBuyer,
  NcBuyerConfig,
  NcBuyerDetails,
  PostNcConfigRes,
} from '../../interfaces/buyer-response.interface';
import { map, tap } from 'rxjs/operators';
import { EnvironmentConfig } from '../../interfaces/environment.interface';
import { AuthService } from '../auth/auth.service';
import { InvoiceOperationService } from '../invoice-operation-services/invoice-operation.service';

@Injectable({
  providedIn: 'root',
})
export class BuyerService {
  private readonly _baseUrl = this._env.environment.pureSpectrum.url;
  private readonly _nightcrawlerUrl =
    this._env.environment.pureSpectrum.nightcrawlerUrl;
  constructor(
    private _http: HttpClient,
    @Inject('ENV') private _env: EnvironmentConfig,
    private _auth: AuthService,
    private _invoiceOperationService: InvoiceOperationService
  ) {}

  getBuyers() {
    return this._http.get<GetBuyerResponse>(`${this._baseUrl}/getBuyers`);
  }

  addNightcrawlerConfigSettings(buyerId: number, payload: AddNCConfigRequest) {
    return this._http.post<AddNCConfigResponse>(
      `${this._baseUrl}/buyers/${buyerId}/nc-config`,
      payload
    );
  }

  getNightcrawlerConfigSettings(buyerId: number) {
    return this._http.get<GetNCConfigResponse>(
      `${this._baseUrl}/buyers/${buyerId}/nc-config`
    );
  }

  sendBuyerRNData(payload: SendRNDataRequest) {
    return this._http.post<SendRNDataResponse>(
      `${this._baseUrl}/sendRNdata`,
      payload
    );
  }

  getBuyerRNdata(buyerId: number) {
    return this._http.get<GendRNDataResponse>(
      `${this._baseUrl}/getRNdata/${buyerId}`
    );
  }

  getNightcrawlerBuyers(token: string) {
    return this._http.get<NcBuyer[]>(`${this._nightcrawlerUrl}/buyers`, {
      headers: {
        skipInterceptor: 'true',
        'access-token': token,
      },
    });
  }
  getNightcrawlerBuyer(buyerId: number, token: string) {
    return this._http.get<NcBuyerDetails>(
      `${this._nightcrawlerUrl}/buyers/${buyerId}`,
      {
        headers: {
          skipInterceptor: 'true',
          'access-token': token,
        },
      }
    );
  }
  syncSurveyToNightcrawler(ncBuyerConfig: NcBuyerConfig) {
    return this._http
      .post<PostNcConfigRes>(
        `${this._baseUrl}/nightcrawler/v1/surveys`,
        ncBuyerConfig
      )
      .pipe(
        map((response) => {
          return response?.psSupplier?.surveyEntryLink;
        })
      );
  }

  getBuyerServiceAccountToken(buyerId?: number) {
    return this._http.get<BuyerServiceAccountTokenRes>(
      `${this._baseUrl}/users/buyer-service-account/token/${buyerId}`
    );
  }

  getReportPreference(userId: number) {
    return this._http
      .get<ReportPreferences[]>(
        `${this._baseUrl}/reports/users/${userId}/preferences`
      )
      .pipe(
        map((reportPreference) =>
          reportPreference.sort((o1, o2) => o1.order - o2.order)
        )
      );
  }

  saveReportPreference(userId: number, payload: ReportPreferencesPayload) {
    return this._http.post<ReportPreferencesPayload>(
      `${this._baseUrl}/reports/users/${userId}/preferences`,
      payload
    );
  }

  downloadTransactionReport(
    surveyId: number | undefined,
    includeTestTransactions: boolean
  ) {
    const _userCompanyId = this._auth.user?.cmp;
    const userType = this._auth.userType;

    return this._invoiceOperationService
      .downloadSTRV3(
        Number(_userCompanyId),
        surveyId?.toString(),
        userType,
        includeTestTransactions
      )
      .pipe(
        tap((resp) => {
          const fileName = resp.fileName;
          window.location.assign(
            this._invoiceOperationService.downloadFile(
              fileName,
              this._auth.token!
            )
          );
        })
      );
  }

  getPDSettings(companyId: number) {
    return this._http.get<GetPDSettingsResponse>(
      `${this._baseUrl}/companies/${companyId}/programmatic-direct-settings`
    );
  }

  downloadCSTransactionReport(surveyId: number | undefined) {
    return this._invoiceOperationService
      .downloadCSTransactionReport(surveyId)
      .pipe(
        tap((resp) => {
          window.location.assign(
            this._invoiceOperationService.downloadFileCSTransactionReport(
              surveyId
            )
          );
        })
      );
  }

  downloadSupplierSummaryReport(surveyId: number | undefined) {
    return this._invoiceOperationService
      .downloadSupplierSummaryReport(surveyId)
      .pipe(
        tap((resp) => {
          window.location.assign(
            this._invoiceOperationService.downloadFileSupplierSummaryReport(
              surveyId
            )
          );
        })
      );
  }
}
